'use client'

import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'

import { Icon, IconComponent, SearchBar, Tree, TreeItem } from '@graftcode/design-system'

import { MOBILE_MENU_TEST_IDS } from '@common/common-test-ids.const'
import { AppVariant } from '@common/enums'

import { BaseMobileMenu } from '../../BaseMobileMenu'
import type { MobileMenuShellProps } from '../../mobileMenuShell.types'

import styles from '../../MobileMenu.module.scss'

const getAllItemIds = (items: TreeItem[]): string[] =>
  items.flatMap((item) => [item.id, ...(item.children ? getAllItemIds(item.children) : [])])

type VisionMobileMenuProps = MobileMenuShellProps & {
  treeItems?: TreeItem[]
  activeItemId?: string
  onNavigationClick?: (url: string) => void
  onProfileClick?: (itemId: string) => void
  onTreeItemClick?: (item: TreeItem) => void
  onTreeItemToggle?: (item: TreeItem, expanded: boolean) => void
  isLoading?: boolean
  isError?: boolean
}

export function VisionMobileMenu({
  isOpen,
  onClose,
  menuGroups,
  helpItems,
  treeItems = [],
  activeItemId,
  onNavigationClick,
  onProfileClick,
  onTreeItemClick,
  onTreeItemToggle,
  isLoading = false,
  isError = false,
}: VisionMobileMenuProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [localExpandedIds, setLocalExpandedIds] = useState<string[]>(['root'])

  const getParentIds = useCallback((itemId: string | undefined): string[] => {
    if (!itemId) return ['root']
    const parts = itemId.split('/')
    const parentIds: string[] = ['root']
    for (let i = 0; i < parts.length; i++) {
      parentIds.push(parts.slice(0, i + 1).join('/'))
    }
    return parentIds
  }, [])

  useEffect(() => {
    if (isOpen && activeItemId) {
      const parentIds = getParentIds(activeItemId)
      setLocalExpandedIds(parentIds)
    }
  }, [activeItemId, getParentIds, isOpen])

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setSearchQuery('')
      }, 300)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isOpen])

  const filteredTreeItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return treeItems
    }

    const filterItems = (items: TreeItem[]): TreeItem[] =>
      items.reduce<TreeItem[]>((acc, item) => {
        const matchesSearch = item.label.toLowerCase().includes(searchQuery.toLowerCase())
        const filteredChildren = item.children ? filterItems(item.children) : []

        if (matchesSearch || filteredChildren.length > 0) {
          acc.push({
            ...item,
            children: filteredChildren.length > 0 ? filteredChildren : item.children,
          })
        }

        return acc
      }, [])

    return filterItems(treeItems)
  }, [searchQuery, treeItems])

  const effectiveExpandedIds = useMemo(() => {
    if (searchQuery.trim()) {
      return getAllItemIds(filteredTreeItems)
    }
    return localExpandedIds
  }, [filteredTreeItems, localExpandedIds, searchQuery])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)

  const handleSearchClear = () => setSearchQuery('')

  const handleItemClick = useCallback(
    (item: TreeItem) => {
      const hasChildren = item.children && item.children.length > 0
      if (hasChildren) {
        setLocalExpandedIds((prev) => {
          if (!prev.includes(item.id)) {
            return [...prev, item.id]
          }
          return prev
        })
      }
      onTreeItemClick?.(item)
    },
    [onTreeItemClick]
  )

  const handleItemToggle = useCallback(
    (item: TreeItem, expanded: boolean) => {
      setLocalExpandedIds((prev) => {
        if (expanded) {
          return [...prev, item.id]
        }
        return prev.filter((id) => id !== item.id)
      })
      onTreeItemToggle?.(item, expanded)
    },
    [onTreeItemToggle]
  )

  const treeKey = `${activeItemId}::${effectiveExpandedIds.join(',')}::${searchQuery}`

  const renderTreeNavigation = () => {
    if (isError) {
      return (
        <div className={styles.visionTreeNoResults} data-testid={MOBILE_MENU_TEST_IDS.visionTreeNoResults}>
          <IconComponent icon={Icon.ErrorCircleRegular} className={styles.visionTreeNoResultsIcon} />
          <span className={styles.visionTreeNoResultsTitle}>Failed to load libraries</span>
          <span className={styles.visionTreeNoResultsMessage}>Please try again later</span>
        </div>
      )
    }

    if (isLoading || treeItems.length === 0) {
      return null
    }

    return (
      <div className={styles.visionTreeContainer}>
        <SearchBar
          className={styles.visionTreeSearch}
          value={searchQuery}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          size="md"
          version="gray"
          placeholder="Search libraries..."
        />
        {filteredTreeItems.length > 0 ? (
          <Tree
            key={treeKey}
            className={styles.visionTree}
            items={filteredTreeItems}
            expandedItemIds={effectiveExpandedIds}
            activeItemId={activeItemId}
            showBadges
            onItemClick={handleItemClick}
            onItemToggle={handleItemToggle}
          />
        ) : searchQuery.trim() ? (
          <div className={styles.visionTreeNoResults}>
            <IconComponent icon={Icon.SearchRegular} className={styles.visionTreeNoResultsIcon} />
            <span className={styles.visionTreeNoResultsTitle}>No results found</span>
            <span className={styles.visionTreeNoResultsMessage}>Try a different search term</span>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div data-testid={MOBILE_MENU_TEST_IDS.visionMenu} style={{ display: 'contents' }}>
      <BaseMobileMenu
        isOpen={isOpen}
        onClose={onClose}
        appVariant={AppVariant.VISION}
        menuGroups={menuGroups}
        helpItems={helpItems}
        onNavigationClick={onNavigationClick}
        onProfileClick={onProfileClick}
        navigationTopSlot={renderTreeNavigation()}
      />
    </div>
  )
}
